import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignProductsInfo } from '@cart-services/hooks/useFpCampaignProductsInfo'
import { DIALOG_VALIDATE_STOCK, type DialogValidateStockProps } from '@cart-ui/DialogValidateStock/DialogValidateStock'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import type { FastpassCampaignItem } from '@dni-analysis/types/fastpass-campaign-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { type SubmitHandler } from 'react-hook-form'
import { useHistory } from 'react-router-dom-v5'
import { DIALOG_GIFT_NOT_ENOUGH, DialogGiftNotEnough } from '../DialogGiftNotEnough/DialogGiftNotEnough'
import { CartFormProvider, type CartFormValues } from './CartFormProvider'

interface CartContextProps {
  setIsGiftNotEnough: (value: boolean) => void
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  currency?: string
  fallbackUrl?: string
  campaignCode: string
  discardAllRedirectUrl?: string
  isSubmitLoading?: boolean
}

export const CartContext = createContext<CartContextProps | null>(null)

export interface CartProviderProps {
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  fallbackUrl?: string
  discardAllRedirectUrl?: string
  checkoutUrl?: string
  currency?: string
  campaignCode: string
}

export const CampaignCartProvider = (props: PropsWithChildren<CartProviderProps>) => {
  const { children, countryCode, projectCode, campaignId, fallbackUrl, discardAllRedirectUrl, checkoutUrl, currency, campaignCode } = props
  const history = useHistory()
  const [isGiftNotEnough, setIsGiftNotEnough] = useState(false)
  const { openDialog: openDialogGiftNotEnough } = useDialog(DIALOG_GIFT_NOT_ENOUGH)
  const { isProductsLoading, getCartItemsStatus, adjustAllCartItemQuantityWithDeficientProduct, productMap } = useFpCampaignProductsInfo({ campaignId, campaignCode, countryCode })
  const { openDialog: openDialogValidateStock } = useDialog<DialogValidateStockProps>(DIALOG_VALIDATE_STOCK)
  const { items: cartItems } = useFpCampaignCartService(campaignId)
  const { calculateResult: campaignCouponCalculateResult } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { sendBeginCheckoutEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const handleSubmit: SubmitHandler<CartFormValues> = useCallbackEvent(async () => {
    const submitAction = () => {
      if (checkoutUrl) {
        if (campaignCode && cartItems.length > 0 && productMap && currency) {
          sendBeginCheckoutEvent({
            campaignCode,
            currency,
            value: campaignCouponCalculateResult?.total || 0,
            items: cartItems.map((item): FastpassCampaignItem => {
              const product = productMap[item.cartItemId]
              return {
                item_id: String(item.cartItemId),
                quantity: item.quantity,
                item_brand: item.project_code,
                item_name: product?.name || '',
                price: Number(product?.sale_price) || 0,
                item_variant: generateItemVariant({
                  productName: product?.name,
                  attributes: item.attributes,
                }),
              }
            }),
          })
        }
        history.push(checkoutUrl)
      }
    }

    const { hasSoldOutProduct, hasDeficientProduct } = await getCartItemsStatus()

    if (hasDeficientProduct) {
      openDialogValidateStock()
      adjustAllCartItemQuantityWithDeficientProduct()
      return
    }

    if (isGiftNotEnough) {
      openDialogGiftNotEnough({
        onSubmit: submitAction,
      })
      return
    }

    if (hasSoldOutProduct) {
      openDialogValidateStock()
      return
    }
    submitAction()
  })

  const contextValue = useMemo(() => ({
    countryCode,
    projectCode,
    campaignId,
    setIsGiftNotEnough,
    currency,
    fallbackUrl,
    campaignCode,
    discardAllRedirectUrl,
    isSubmitLoading: isProductsLoading,
  }), [countryCode, projectCode, campaignId, currency, fallbackUrl, campaignCode, discardAllRedirectUrl, isProductsLoading])

  return (
    <CartContext.Provider value={contextValue}>
      <CartFormProvider onSubmit={handleSubmit}>
        <DialogGiftNotEnough />
        {children}
      </CartFormProvider>
    </CartContext.Provider>
  )
}

export const useContextCampaignCart = () => {
  const context = useContext(CartContext)
  if (!context) throw new Error('useContextCampaignCart must be used within a CartProvider')
  return context
}
